import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import * as faceapi from 'face-api.js';
import { 
  Clock, AlertTriangle, CheckCircle, X, ArrowLeft, Check,
  ArrowRight, AlertCircle, Info, Timer, Camera, Mic, 
  MonitorSmartphone, Shield, Eye, AlertOctagon
} from 'lucide-react';

interface Question {
  id: number;
  text: string;
  options: string[];
  correctAnswer: number; // Index of correct option
}

interface Assessment {
  id: string;
  title: string;
  courseId: string;
  courseName: string;
  description: string;
  duration: number; // in minutes
  totalQuestions: number;
  passingScore: number;
  questions: Question[];
}

interface ProctorState {
  webcamEnabled: boolean;
  audioEnabled: boolean;
  webcamStream: MediaStream | null;
  audioStream: MediaStream | null;
  isLoadingDevices: boolean;
  deviceError: string | null;
  faceCount: number;
  lastFaceDetection: number;
  externalDisplayDetected: boolean;
  suspiciousActivities: string[];
}

export const TakeAssessment = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [remainingTime, setRemainingTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [warningCount, setWarningCount] = useState(0);
  const [isTestComplete, setIsTestComplete] = useState(false);
  const [result, setResult] = useState<{
    score: number;
    totalQuestions: number;
    correctAnswers: number;
    isPassed: boolean;
  } | null>(null);
  
  const [proctorState, setProctorState] = useState<ProctorState>({
    webcamEnabled: false,
    audioEnabled: false,
    webcamStream: null,
    audioStream: null,
    isLoadingDevices: false,
    deviceError: null,
    faceCount: 0,
    lastFaceDetection: Date.now(),
    externalDisplayDetected: false,
    suspiciousActivities: [],
  });
  
  const [showProctorDialog, setShowProctorDialog] = useState(true);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  
  const timerRef = useRef<any | null>(null);
  const visibilityWarningsRef = useRef(0);
  const documentRef = useRef<Document | null>(null);
  const webcamRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const faceDetectionIntervalRef = useRef<any | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioAnalyserRef = useRef<AnalyserNode | null>(null);

  useEffect(() => {
    setIsLoading(true);

    setTimeout(() => {
      const mockAssessment: Assessment = {
        id: '1',
        title: 'Python Full Stack Developer Assessment',
        courseId: id || '1',
        courseName: 'Python Full Stack Developer',
        description: 'This assessment evaluates your understanding of full stack development concepts including HTML, CSS, JavaScript, React, and Python.',
        duration: 60, // 60 minutes
        totalQuestions: 30,
        passingScore: 70,
        questions: generateMockQuestions(30)
      };

      setAssessment(mockAssessment);
      setRemainingTime(mockAssessment.duration * 60);
      setSelectedAnswers(new Array(mockAssessment.totalQuestions).fill(-1));
      setIsLoading(false);
    }, 1000);

    const loadModels = async () => {
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
        await faceapi.nets.faceLandmark68Net.loadFromUri('/models');
        await faceapi.nets.faceRecognitionNet.loadFromUri('/models');
        setModelsLoaded(true);
      } catch (error) {
        console.error('Error loading face detection models:', error);
        toast({
          title: "Warning",
          description: "Face detection features couldn't be loaded. You can still take the assessment, but some proctoring features will be disabled.",
          variant: "destructive"
        });
        setModelsLoaded(true);
      }
    };

    loadModels();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (faceDetectionIntervalRef.current) clearInterval(faceDetectionIntervalRef.current);
      if (proctorState.webcamStream) {
        proctorState.webcamStream.getTracks().forEach(track => track.stop());
      }
      if (proctorState.audioStream) {
        proctorState.audioStream.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current?.state !== 'closed') {
        audioContextRef.current?.close();
      }
    };
  }, [id, toast]);

  useEffect(() => {
    console.log('In a production environment, you need to serve face-api.js models from your public folder at /models');
  }, []);

  const enableWebcam = async () => {
    try {
      setProctorState(prev => ({ ...prev, isLoadingDevices: true, deviceError: null }));
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: {
          width: 640,
          height: 480,
          facingMode: 'user'
        }
      });
      
      if (webcamRef.current) {
        webcamRef.current.srcObject = stream;
        webcamRef.current.onloadedmetadata = () => {
          webcamRef.current?.play().catch(e => console.error("Error playing video:", e));
        };
      }
      
      setProctorState(prev => ({ 
        ...prev, 
        webcamEnabled: true,
        webcamStream: stream,
        isLoadingDevices: false
      }));
      
      if (modelsLoaded) {
        startFaceDetection();
      }
      
      toast({
        title: "Webcam Enabled",
        description: "Your webcam is now being monitored during the assessment.",
      });
      
    } catch (error) {
      console.error('Error accessing webcam:', error);
      setProctorState(prev => ({ 
        ...prev, 
        deviceError: 'Could not access webcam. Please ensure you have granted permission and your camera is working.',
        isLoadingDevices: false
      }));
      
      toast({
        title: "Webcam Error",
        description: "Could not access webcam. Please ensure you have granted permission and your device has a working camera.",
        variant: "destructive"
      });
    }
  };
  
  const enableAudio = async () => {
    try {
      setProctorState(prev => ({ ...prev, isLoadingDevices: true, deviceError: null }));
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const audioContext = new AudioContext();
      const audioSource = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      audioSource.connect(analyser);
      
      audioContextRef.current = audioContext;
      audioAnalyserRef.current = analyser;
      
      setProctorState(prev => ({ 
        ...prev, 
        audioEnabled: true,
        audioStream: stream,
        isLoadingDevices: false
      }));
      
      monitorAudioLevels();
      
      toast({
        title: "Microphone Enabled",
        description: "Your microphone is now being monitored during the assessment.",
      });
      
    } catch (error) {
      console.error('Error accessing microphone:', error);
      setProctorState(prev => ({ 
        ...prev, 
        deviceError: 'Could not access microphone. Please ensure you have granted permission and your microphone is working.',
        isLoadingDevices: false
      }));
      
      toast({
        title: "Microphone Error",
        description: "Could not access microphone. Please ensure you have granted permission and your device has a working microphone.",
        variant: "destructive"
      });
    }
  };
  
  const monitorAudioLevels = () => {
    if (!audioAnalyserRef.current) return;
    
    const analyser = audioAnalyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const checkAudio = () => {
      analyser.getByteFrequencyData(dataArray);
      
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const avg = sum / dataArray.length;
      
      if (avg > 50) {
        logSuspiciousActivity('High volume detected, possible conversation');
      }
      
      if (!isTestComplete) {
        requestAnimationFrame(checkAudio);
      }
    };
    
    checkAudio();
  };
  
  const startFaceDetection = () => {
    if (!webcamRef.current || !canvasRef.current || !modelsLoaded) return;
    
    if (webcamRef.current.paused && webcamRef.current.srcObject) {
      webcamRef.current.play().catch(e => console.error("Error playing video:", e));
    }
    
    faceDetectionIntervalRef.current = setInterval(async () => {
      if (!webcamRef.current || !canvasRef.current || !proctorState.webcamEnabled) return;
      
      try {
        const detections = await faceapi.detectAllFaces(
          webcamRef.current,
          new faceapi.TinyFaceDetectorOptions()
        );
        
        const faceCount = detections.length;
        
        setProctorState(prev => ({ ...prev, faceCount, lastFaceDetection: Date.now() }));
        
        const displaySize = { 
          width: webcamRef.current.videoWidth, 
          height: webcamRef.current.videoHeight 
        };
        faceapi.matchDimensions(canvasRef.current, displaySize);
        const resizedDetections = faceapi.resizeResults(detections, displaySize);
        
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          faceapi.draw.drawDetections(canvasRef.current, resizedDetections);
        }
        
        if (faceCount > 1) {
          logSuspiciousActivity(`Multiple people detected (${faceCount} faces)`);
          
          toast({
            title: "Warning: Multiple People Detected",
            description: `We detected ${faceCount} faces in the webcam. Please ensure only the candidate is present.`,
            variant: "destructive"
          });
        }
        
        if (faceCount === 0) {
          const timeSinceLastFace = Date.now() - proctorState.lastFaceDetection;
          if (faceCount > 0 || timeSinceLastFace < 5000) return;
          
          logSuspiciousActivity('No face detected, candidate may be away');
          
          toast({
            title: "Warning: No Face Detected",
            description: "We can't detect your face. Please ensure you are visible in the webcam.",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error during face detection:', error);
      }
    }, 2000);
  };
  
  const checkForExternalDisplays = async () => {
    try {
      const screenWithExtended = window.screen as any;
      if (screenWithExtended.isExtended) {
        setProctorState(prev => ({ ...prev, externalDisplayDetected: true }));
        logSuspiciousActivity('External display detected');
        
        toast({
          title: "Warning: External Display Detected",
          description: "We detected an external display. Please disconnect any additional monitors.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.log('External display detection not supported in this browser');
    }
  };
  
  const logSuspiciousActivity = (activity: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `${timestamp}: ${activity}`;
    
    setProctorState(prev => ({
      ...prev,
      suspiciousActivities: [...prev.suspiciousActivities, logEntry]
    }));
    
    console.log('Suspicious activity:', logEntry);
  };

  useEffect(() => {
    if (isLoading || isTestComplete || showProctorDialog) return;

    timerRef.current = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          handleSubmitAssessment();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const handleVisibilityChange = () => {
      if (document.hidden && !isTestComplete) {
        visibilityWarningsRef.current += 1;
        logSuspiciousActivity('Left assessment tab/window');
        
        if (visibilityWarningsRef.current <= 2) {
          setIsWarningDialogOpen(true);
          setWarningCount(visibilityWarningsRef.current);
          
          toast({
            title: "Warning!",
            description: `Leaving the assessment area is not allowed. Warning ${visibilityWarningsRef.current}/3.`,
            variant: "destructive"
          });
        } else {
          handleSubmitAssessment(true);
          
          toast({
            title: "Assessment Terminated",
            description: "You've left the assessment area too many times. Your assessment has been submitted.",
            variant: "destructive"
          });
        }
      }
    };

    checkForExternalDisplays();

    const preventCopyPaste = (e: ClipboardEvent) => {
      e.preventDefault();
      logSuspiciousActivity('Attempted to copy/paste');
      toast({
        title: "Action Not Allowed",
        description: "Copy and paste functions are disabled during the assessment.",
        variant: "destructive"
      });
    };

    const handleDeviceChange = () => {
      logSuspiciousActivity('USB device connected or disconnected');
      toast({
        title: "Device Change Detected",
        description: "USB device changes have been detected and logged.",
        variant: "destructive"
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    
    const navigatorWithUSB = navigator as Navigator & { usb?: { addEventListener: Function; removeEventListener: Function } };
    if (navigatorWithUSB.usb) {
      navigatorWithUSB.usb.addEventListener('connect', handleDeviceChange);
      navigatorWithUSB.usb.addEventListener('disconnect', handleDeviceChange);
    }

    documentRef.current = document;

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (documentRef.current) {
        documentRef.current.removeEventListener('visibilitychange', handleVisibilityChange);
        documentRef.current.removeEventListener('copy', preventCopyPaste);
        documentRef.current.removeEventListener('paste', preventCopyPaste);
        documentRef.current.removeEventListener('cut', preventCopyPaste);
      }
      if (navigatorWithUSB.usb) {
        navigatorWithUSB.usb.removeEventListener('connect', handleDeviceChange);
        navigatorWithUSB.usb.removeEventListener('disconnect', handleDeviceChange);
      }
    };
  }, [isLoading, isTestComplete, showProctorDialog, toast]);

  const handleStartAssessment = () => {
    if (!proctorState.webcamEnabled || !proctorState.audioEnabled) {
      toast({
        title: "Cannot Start Assessment",
        description: "Both webcam and microphone must be enabled before starting the assessment.",
        variant: "destructive"
      });
      return;
    }
    
    setShowProctorDialog(false);
    
    toast({
      title: "Assessment Started",
      description: "Do not leave this page or switch tabs during the assessment.",
    });
  };

  const formatTime = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSelectOption = (optionIndex: number) => {
    const newSelectedAnswers = [...selectedAnswers];
    newSelectedAnswers[currentQuestion] = optionIndex;
    setSelectedAnswers(newSelectedAnswers);
  };

  const handleNextQuestion = () => {
    if (currentQuestion < (assessment?.totalQuestions || 0) - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmitAssessment = (isForced = false) => {
    if (timerRef.current) clearInterval(timerRef.current);

    if (!isForced && !isConfirmDialogOpen && selectedAnswers.includes(-1)) {
      toast({
        title: "Incomplete Assessment",
        description: "You have unanswered questions. Are you sure you want to submit?",
        variant: "destructive"
      });
      setIsConfirmDialogOpen(true);
      return;
    }

    setIsSubmitting(true);

    if (assessment) {
      const correctAnswers = selectedAnswers.reduce((count, answer, index) => {
        return answer === assessment.questions[index].correctAnswer ? count + 1 : count;
      }, 0);

      const score = Math.round((correctAnswers / assessment.totalQuestions) * 100);
      const isPassed = score >= assessment.passingScore;

      setResult({
        score,
        totalQuestions: assessment.totalQuestions,
        correctAnswers,
        isPassed
      });

      setIsTestComplete(true);
      
      if (proctorState.webcamStream) {
        proctorState.webcamStream.getTracks().forEach(track => track.stop());
      }
      if (proctorState.audioStream) {
        proctorState.audioStream.getTracks().forEach(track => track.stop());
      }
      if (faceDetectionIntervalRef.current) {
        clearInterval(faceDetectionIntervalRef.current);
      }
    }

    setIsSubmitting(false);
    setIsConfirmDialogOpen(false);
  };

  const generateMockQuestions = (count: number): Question[] => {
    const questions: Question[] = [];
    
    const questionTemplates = [
      {
        text: "Which HTML tag is used to create a hyperlink?",
        options: ["<link>", "<a>", "<href>", "<url>"],
        correctAnswer: 1
      },
      {
        text: "Which of the following is NOT a semantic HTML5 element?",
        options: ["<article>", "<section>", "<div>", "<aside>"],
        correctAnswer: 2
      },
      {
        text: "What does HTML stand for?",
        options: [
          "Hyper Text Markup Language", 
          "Highly Typed Modern Language", 
          "Home Tool Markup Language", 
          "Hyperlink and Text Markup Language"
        ],
        correctAnswer: 0
      },
      
      {
        text: "Which CSS property is used to change the text color?",
        options: ["text-color", "font-color", "color", "text-style"],
        correctAnswer: 2
      },
      {
        text: "Which CSS selector has the highest specificity?",
        options: [".class", "#id", "element", "*"],
        correctAnswer: 1
      },
      {
        text: "What is the default value of the 'position' property?",
        options: ["relative", "absolute", "fixed", "static"],
        correctAnswer: 3
      },
      
      {
        text: "What will '2' + 2 evaluate to in JavaScript?",
        options: ["4", "22", "'22'", "TypeError"],
        correctAnswer: 2
      },
      {
        text: "Which method is used to add an element at the end of an array?",
        options: ["push()", "append()", "add()", "insert()"],
        correctAnswer: 0
      },
      {
        text: "What does the 'typeof null' return in JavaScript?",
        options: ["null", "undefined", "object", "number"],
        correctAnswer: 2
      },
      
      {
        text: "What is the correct way to create a function in Python?",
        options: [
          "function myFunc():", 
          "def myFunc():", 
          "create myFunc():", 
          "function = myFunc()"
        ],
        correctAnswer: 1
      },
      {
        text: "Which of these is NOT a built-in data type in Python?",
        options: ["list", "dictionary", "array", "tuple"],
        correctAnswer: 2
      },
      {
        text: "What is the output of print(type([]))?",
        options: ["<class 'list'>", "<class 'array'>", "<class 'tuple'>", "<class 'dict'>"],
        correctAnswer: 0
      }
    ];
    
    for (let i = 0; i < count; i++) {
      const templateIndex = i % questionTemplates.length;
      questions.push({
        id: i + 1,
        ...questionTemplates[templateIndex]
      });
    }
    
    return questions;
  };

  const calculateProgress = () => {
    if (!assessment) return 0;
    return (selectedAnswers.filter(answer => answer !== -1).length / assessment.totalQuestions) * 100;
  };

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-3xl">
          <div className="h-8 bg-bytesplash-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-bytesplash-gray-200 rounded w-1/2"></div>
          <div className="h-64 bg-bytesplash-gray-200 rounded"></div>
          <div className="h-12 bg-bytesplash-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="p-8 flex justify-center">
        <Card className="w-full max-w-3xl">
          <CardHeader>
            <CardTitle>Assessment Not Found</CardTitle>
          </CardHeader>
          <CardContent>
            <p>The assessment you're looking for could not be found.</p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/assessments')}>
              Back to Assessments
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (showProctorDialog) {
    return (
      <div className="p-8 flex justify-center">
        <Card className="w-full max-w-4xl">
          <CardHeader className="border-b">
            <CardTitle>{assessment.title} - Proctor Setup</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-500 mt-0.5" size={20} />
                <div>
                  <h3 className="font-medium text-yellow-700">Important Instructions</h3>
                  <p className="text-sm text-yellow-600">
                    This assessment requires webcam and microphone access for proctoring. 
                    Please ensure you are in a quiet, well-lit environment with a stable internet connection.
                    You cannot start the assessment until you enable both devices.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Camera size={18} /> Webcam Setup
                  </h3>
                  <Badge variant={proctorState.webcamEnabled ? "success" : "outline"}>
                    {proctorState.webcamEnabled ? 'Enabled' : 'Required'}
                  </Badge>
                </div>
                
                <div className="aspect-video bg-black rounded-lg overflow-hidden relative mb-4">
                  {proctorState.webcamEnabled ? (
                    <>
                      <video 
                        ref={webcamRef}
                        autoPlay 
                        playsInline 
                        muted 
                        className="w-full h-full object-cover"
                      />
                      <canvas 
                        ref={canvasRef}
                        className="absolute top-0 left-0 w-full h-full"
                      />
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-white">
                      <Camera size={48} className="mb-2 opacity-50" />
                      <p className="text-sm opacity-70">Camera preview will appear here</p>
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={enableWebcam}
                  disabled={proctorState.webcamEnabled || proctorState.isLoadingDevices}
                  className="w-full"
                >
                  {proctorState.isLoadingDevices && !proctorState.webcamEnabled 
                    ? 'Enabling Camera...' 
                    : proctorState.webcamEnabled 
                      ? 'Camera Enabled' 
                      : 'Enable Camera'}
                </Button>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium flex items-center gap-2">
                    <Mic size={18} /> Microphone Setup
                  </h3>
                  <Badge variant={proctorState.audioEnabled ? "success" : "outline"}>
                    {proctorState.audioEnabled ? 'Enabled' : 'Required'}
                  </Badge>
                </div>
                
                <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden flex flex-col items-center justify-center mb-4">
                  {proctorState.audioEnabled ? (
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                        <Mic className="text-green-500" size={32} />
                      </div>
                      <p className="text-green-600 font-medium">Microphone Active</p>
                      <p className="text-sm text-gray-500">Audio is being monitored</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-2">
                        <Mic className="text-gray-400" size={32} />
                      </div>
                      <p className="text-gray-600">Microphone Inactive</p>
                      <p className="text-sm text-gray-500">Click button below to enable</p>
                    </div>
                  )}
                </div>
                
                <Button 
                  onClick={enableAudio}
                  disabled={proctorState.audioEnabled || proctorState.isLoadingDevices}
                  className="w-full"
                  variant="outline"
                >
                  {proctorState.isLoadingDevices && !proctorState.audioEnabled
                    ? 'Enabling Microphone...'
                    : proctorState.audioEnabled
                      ? 'Microphone Enabled'
                      : 'Enable Microphone'}
                </Button>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-red-500" />
                <span>{proctorState.deviceError}</span>
              </div>
            </div>
            
            <div className="bg-bytesplash-light/30 rounded-lg p-4">
              <h3 className="font-medium mb-2 flex items-center gap-2">
                <Shield size={18} className="text-bytesplash-primary" /> 
                Proctoring Information
              </h3>
              <div className="space-y-2">
                <div className="flex items-start gap-2 text-sm">
                  <Eye size={16} className="mt-0.5 text-bytesplash-primary" />
                  <p>Your webcam will be used to ensure only you are taking the assessment.</p>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <Mic size={16} className="mt-0.5 text-bytesplash-primary" />
                  <p>Your microphone will be used to detect conversations or unusual sounds.</p>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <MonitorSmartphone size={16} className="mt-0.5 text-bytesplash-primary" />
                  <p>Multiple monitors and USB devices will be detected and logged.</p>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <AlertOctagon size={16} className="mt-0.5 text-bytesplash-primary" />
                  <p>Leaving the tab or window will trigger warnings and may terminate your assessment.</p>
                </div>
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t p-4 flex justify-between items-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/assessments')}
            >
              Cancel Assessment
            </Button>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="default" 
                onClick={handleStartAssessment}
                disabled={!proctorState.webcamEnabled || !proctorState.audioEnabled}
              >
                Start Assessment
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (isTestComplete) {
    return (
      <div className="p-8 flex justify-center">
        <Card className="w-full max-w-3xl">
          <CardHeader className="text-center border-b">
            <CardTitle>Assessment Complete</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {result && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-bytesplash-light mb-4">
                    {result.isPassed ? (
                      <CheckCircle className="h-12 w-12 text-green-600" />
                    ) : (
                      <X className="h-12 w-12 text-red-600" />
                    )}
                  </div>
                  <h2 className="text-2xl font-bold">
                    {result.isPassed ? "Congratulations!" : "Assessment Failed"}
                  </h2>
                  <p className="text-muted-foreground">
                    {result.isPassed 
                      ? "You have successfully passed the assessment." 
                      : "You did not meet the minimum passing score for this assessment."}
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Score:</span>
                    <span className="text-xl font-semibold">{result.score}%</span>
                  </div>
                  
                  <Progress value={result.score} className="h-2" />
                  
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="border rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-bytesplash-primary">{result.correctAnswers}</div>
                      <div className="text-sm text-muted-foreground">Correct Answers</div>
                    </div>
                    <div className="border rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-bytesplash-primary">{result.totalQuestions}</div>
                      <div className="text-sm text-muted-foreground">Total Questions</div>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Info size={18} className="text-bytesplash-primary" />
                      <h3 className="font-medium">Passing Score</h3>
                    </div>
                    <p className="text-sm">
                      The minimum passing score for this assessment is {assessment.passingScore}%. 
                      {result.isPassed 
                        ? " You have successfully passed this assessment." 
                        : " You may retake this assessment after reviewing the course material."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="border-t p-4 flex justify-between">
            <Button 
              variant="outline" 
              onClick={() => navigate('/courses/' + assessment.courseId)}
            >
              Back to Course
            </Button>
            <Button 
              variant="default" 
              onClick={() => navigate('/assessments')}
            >
              View All Assessments
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 flex flex-col">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold">{assessment.title}</h1>
          <p className="text-muted-foreground">{assessment.courseName}</p>
        </div>
        
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-2 bg-bytesplash-light/30 px-3 py-1.5 rounded-full">
            <Clock size={16} className="text-bytesplash-primary" />
            <span className="font-medium">{formatTime(remainingTime)}</span>
          </div>
          <Badge variant="outline" className="gap-1">
            <Timer size={14} /> {assessment.duration} min
          </Badge>
        </div>
      </div>
      
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        <div className="xl:col-span-3 space-y-6">
          <Card className="overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">Progress</h3>
                <span className="text-sm text-muted-foreground">
                  {selectedAnswers.filter(a => a !== -1).length} of {assessment.totalQuestions} questions answered
                </span>
              </div>
              <Progress value={calculateProgress()} className="h-2" />
            </div>
            <div className="p-4 grid grid-cols-5 sm:grid-cols-10 gap-2">
              {assessment.questions.map((_, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  className={`question-button h-10 w-10 p-0 ${
                    index === currentQuestion
                      ? "active"
                      : selectedAnswers[index] !== -1
                      ? "answered"
                      : "unanswered"
                  }`}
                  onClick={() => setCurrentQuestion(index)}
                >
                  {index + 1}
                </Button>
              ))}
            </div>
          </Card>
          
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-bytesplash-light text-bytesplash-primary text-sm font-medium">
                  {currentQuestion + 1}
                </span>
                Question {currentQuestion + 1} of {assessment.totalQuestions}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="mb-6">
                <p className="text-lg font-medium">{assessment.questions[currentQuestion].text}</p>
              </div>
              
              <div className="space-y-3">
                {assessment.questions[currentQuestion].options.map((option, index) => (
                  <div 
                    key={index}
                    onClick={() => handleSelectOption(index)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedAnswers[currentQuestion] === index
                        ? "border-bytesplash-primary bg-bytesplash-light/30"
                        : "hover:border-bytesplash-primary/30 hover:bg-bytesplash-light/10"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                        selectedAnswers[currentQuestion] === index
                          ? "border-bytesplash-primary"
                          : "border-bytesplash-gray-300"
                      }`}>
                        {selectedAnswers[currentQuestion] === index && (
                          <div className="h-3 w-3 rounded-full bg-bytesplash-primary" />
                        )}
                      </div>
                      <span>{option}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
            <CardFooter className="border-t p-4 flex justify-between">
              <Button
                variant="outline"
                onClick={handlePrevQuestion}
                disabled={currentQuestion === 0}
                className="flex items-center gap-2"
              >
                <ArrowLeft size={16} /> Previous
              </Button>
              
              {currentQuestion === assessment.totalQuestions - 1 ? (
                <Button
                  onClick={() => setIsConfirmDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  Submit Assessment <Check size={16} />
                </Button>
              ) : (
                <Button
                  onClick={handleNextQuestion}
                  className="flex items-center gap-2"
                >
                  Next <ArrowRight size={16} />
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>
        
        <div className="space-y-4">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="text-sm flex items-center gap-2">
                <Camera size={16} className="text-bytesplash-primary" /> 
                Webcam Monitoring
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="webcam-container bg-black rounded-lg overflow-hidden aspect-video mb-2 relative">
                <video 
                  ref={webcamRef}
                  autoPlay 
                  playsInline 
                  muted 
                  className="w-full h-full object-cover"
                />
                <canvas
                  ref={canvasRef}
                  className="absolute top-0 left-0 w-full h-full"
                />
              </div>
              <p className="text-xs text-center text-muted-foreground mt-2">
                Your webcam is being monitored during the assessment
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="border-b py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield size={16} className="text-bytesplash-primary" /> 
                Proctoring Status
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <Camera size={14} /> Webcam
                  </span>
                  <Badge variant="success" className="text-xs py-0 px-2">Active</Badge>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <Mic size={14} /> Microphone
                  </span>
                  <Badge variant="success" className="text-xs py-0 px-2">Active</Badge>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <MonitorSmartphone size={14} /> External Display
                  </span>
                  <Badge 
                    variant={proctorState.externalDisplayDetected ? "error" : "success"} 
                    className="text-xs py-0 px-2"
                  >
                    {proctorState.externalDisplayDetected ? 'Detected' : 'None'}
                  </Badge>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1.5">
                    <AlertOctagon size={14} /> Warnings
                  </span>
                  <Badge 
                    variant={warningCount > 0 ? (warningCount > 1 ? "error" : "warning") : "success"} 
                    className="text-xs py-0 px-2"
                  >
                    {warningCount}/3
                  </Badge>
                </div>
              </div>
              
              {proctorState.suspiciousActivities.length > 0 && (
                <div className="mt-4 border-t pt-3">
                  <h4 className="text-xs font-medium mb-2">Activity Log</h4>
                  <div className="space-y-2 max-h-24 overflow-y-auto text-xs">
                    {proctorState.suspiciousActivities.slice(-3).map((activity, i) => (
                      <div key={i} className="bg-bytesplash-light/20 p-2 rounded text-xs">
                        {activity}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Assessment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to submit your assessment?</p>
            
            {selectedAnswers.includes(-1) && (
              <div className="flex items-start gap-2 mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="text-yellow-500 mt-0.5" size={16} />
                <div className="text-sm text-yellow-700">
                  <p className="font-medium">You have unanswered questions</p>
                  <p>
                    {selectedAnswers.filter(a => a === -1).length} out of {assessment.totalQuestions} questions are unanswered. 
                    You won't be able to change your answers after submission.
                  </p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleSubmitAssessment()}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={isWarningDialogOpen} onOpenChange={setIsWarningDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle /> Warning
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-4">You left the assessment area. This activity has been logged.</p>
            
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertOctagon className="text-red-500 mt-0.5" size={16} />
                <div className="text-sm text-red-700">
                  <p className="font-medium">Warning {warningCount}/3</p>
                  <p>
                    Leaving the assessment area is not permitted. After 3 warnings, your assessment 
                    will be automatically submitted.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsWarningDialogOpen(false)}>
              I Understand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
